import {useState, useEffect, useCallback} from 'react';
import {useHistory, useParams} from 'react-router';
import _ from 'lodash';
import axios from 'axios';
import API from '../../../../network/dashboardAPI';
import UploadService from '../../../../services/FileUploadService';
import {getValue} from '../../../../helper/constant';
import {baseUrl} from '../../../../constants/Constant';
import useAlert from '../../../../services/useAlert';

const initialCondition = {
	field: '',
	option: '',
	value: [],
};

const initialModify = {
	groups: {
		delete: [],
		update: [],
		add: [],
	},
	product_selection: {
		delete: [],
		update: [],
		add: [],
	},
};

const useSelectionPage = () => {
	const history = useHistory();
	const params = useParams();
	const alert = useAlert();
	// Page state
	const [blocks, setBlocks] = useState([]);
	const [oldData, setOldData] = useState([]);
	const [link, setLink] = useState('');
	const [pageType, setPageType] = useState('');
	const [type, setType] = useState('');
	const [pageId, setPageId] = useState('');
	const [loading, setLoading] = useState(false);
	const [itemCount, setItemCount] = useState([]);

	// Form fields
	const [formData, setFormData] = useState({
		title: '',
		slug: '',
		description: '',
		banner: '',
		small_banner: '',
		logo: '',
		status: 0,
		is_full_width: 1,
		show_on_menu: 1,
		order: 1,
		short_name: '',
		poster: '',
		landing_page_type: '',
		entityIds: null,
	});

	// Modify tracking
	const [modify, setModify] = useState(initialModify);
	const [count, setCount] = useState(0);

	// Initialize page
	useEffect(() => {
		setPageType(params?.page);
		setType(params?.type);
		const id = getValue('id');
		if (id) {
			setPageId(id);
			if (params?.type === 'update') {
				getProductSelectionById(id);
			}
		}
	}, [params]);

	// Reset all data
	const resetData = useCallback(() => {
		setBlocks([]);
		setOldData([]);
		setModify(initialModify);
	}, []);

	// Update form field
	const updateFormField = useCallback((field, value) => {
		setFormData((prev) => ({...prev, [field]: value}));
	}, []);

	const handleChangeEntityType = useCallback((selectedOption) => {
		setFormData((prev) => ({
			...prev,
			entityIds: selectedOption, // Store single object, not array
		}));
	}, []);

	const handleRemoveEntityType = useCallback((removedItem) => {
		setFormData((prev) => ({
			...prev,
			entityIds: null, // Clear single selection
		}));
	}, []);

	const handleAddSKU = useCallback((blockId, conditionIndex, fieldType, {selectedList}) => {
		handleMultiSelectChange(blockId, conditionIndex, fieldType, selectedList);
	}, []);

	// Generate slug from title
	const generateSlug = useCallback(() => {
		const slug = formData.title
			.toLowerCase()
			.trim()
			.replace(/[^\w\s-]/g, '')
			.replace(/\s+/g, '-')
			.replace(/-+/g, '-');
		updateFormField('slug', slug);
	}, [formData.title, updateFormField]);

	// Upload banner/logo
	const uploadImage = useCallback(async (file, type) => {
		if (!file) return;

		const formDataUpload = new FormData();
		formDataUpload.append('image', file);

		try {
			const response = await UploadService.uploadImageFromDashboard(formDataUpload);
			if (response.data.success) {
				const url = response.data.data[0].url;
				const fieldMap = {
					logo: 'logo',
					small_banner: 'small_banner',
					banner: 'banner',
					poster: "poster"
				};
				updateFormField(fieldMap[type], url);
			}
		} catch (error) {
			console.error('Error uploading image:', error);
		}
	}, [updateFormField]);

	// Block operations
	const addBlock = useCallback(() => {
		let item = null;
		if (count === 0) {
			item = _.maxBy(oldData, 'id');
			setCount(1);
		} else {
			item = _.maxBy(blocks, 'id');
		}

		const newBlock = {
			id: item ? item.id + 1 : 1,
			conditions: [{...initialCondition}],
		};

		setBlocks((prev) => [...prev, newBlock]);
		setModify((prev) => ({
			...prev,
			groups: {
				...prev.groups,
				add: [...prev.groups.add, newBlock],
			},
		}));
	}, [blocks, oldData, count]);

	const removeBlock = useCallback((blockId) => {
		const item = blocks.find((block) => block.id === blockId);
		const exist = modify.groups.add.some((group) => group.id === item?.id);

		if (!exist && item) {
			setModify((prev) => ({
				...prev,
				groups: {
					...prev.groups,
					delete: [...prev.groups.delete, item],
				},
			}));
		}

		setBlocks((prev) => prev.filter((block) => block.id !== blockId));
	}, [blocks, modify.groups.add]);

	// Condition operations
	const addCondition = useCallback((blockId) => {
		setBlocks((prev) =>
			prev.map((block) =>
				block.id === blockId
					? {
						...block,
						conditions: [...block.conditions, {...initialCondition}],
					}
					: block
			)
		);
	}, []);

	const removeCondition = useCallback((blockId, conditionIndex, condition) => {
		const group = blocks.filter((block) => block.id === blockId);

		if (group.length > 0) {
			setModify((prev) => ({
				...prev,
				product_selection: {
					...prev.product_selection,
					delete: [...prev.product_selection.delete, condition],
				},
			}));
		}

		setBlocks((prev) =>
			prev.map((block) =>
				block.id === blockId
					? {
						...block,
						conditions: block.conditions.filter((_, idx) => idx !== conditionIndex),
					}
					: block
			)
		);
	}, [blocks]);

	const updateCondition = useCallback((blockId, conditionIndex, key, value) => {
		setBlocks((prev) =>
			prev.map((block) =>
				block.id === blockId
					? {
						...block,
						conditions: block.conditions.map((condition, idx) =>
							idx === conditionIndex ? {...condition, [key]: value} : condition
						),
					}
					: block
			)
		);
	}, []);

	// Handle SKU/Multi-select operations
	const handleMultiSelectChange = useCallback((blockId, conditionIndex, field, selectedList) => {
		setBlocks((prev) =>
			prev.map((block) =>
				block.id === blockId
					? {
						...block,
						conditions: block.conditions.map((condition, idx) =>
							idx === conditionIndex && condition.field === field
								? {
									...condition,
									value: selectedList?.map((val) => ({
										value: val?.value,
										id: val?.id,
									})),
								}
								: condition
						),
					}
					: block
			)
		);
	}, []);

	// Check item OOS status
	const checkItemOOS = useCallback(async (head, item) => {
		let oos = false;
		let is_available = true;

		if (['Product SKU', 'Product ID'].includes(head)) {
			try {
				const response = await axios.get(`${baseUrl}api/v1/product/findOOSItem/${item}`);
				if (response?.data?.store_items_price_types[0]?.available_quantity <= 0) {
					oos = true;
				}
				if (response?.data?.store_items_price_types[0]?.is_available === 0) {
					is_available = false;
				}
			} catch (error) {
				console.error('Error checking OOS:', error);
			}
		}

		return {oos, is_available};
	}, []);

	// Format values for multi-select
	const formatValuesForMultiSelect = useCallback(async (values, head) => {
		if (!values) return [];

		try {
			return await Promise.all(
				values.map(async (value) => {
					const {oos, is_available} = await checkItemOOS(head, value?.id);
					return {
						head,
						label: value?.value,
						key: head,
						value: value?.value,
						id: value?.id,
						icon: '',
						oos,
						is_available,
					};
				})
			);
		} catch (error) {
			console.error('Error formatting values:', error);
			return [];
		}
	}, [checkItemOOS]);

	// Check item count
	const checkItemCount = useCallback((id) => {
		const data = itemCount?.find((item) => item.id === id);
		return data?.count || 0;
	}, [itemCount]);

	// Get selection page by ID
	const getProductSelectionById = useCallback(async (id) => {
		setLoading(true);
		try {
			const response = await API.adminGetProductsSelectionById(id);
			const result = await response.json();

			if (result.success) {
				const item = result.data.data.data[0];
				setBlocks(item?.selection_groups || []);
				setOldData(item?.selection_groups || []);
				setItemCount(result.data?.data?.count || []);

				setFormData({
					title: item?.title || '',
					short_name: item?.short_name || '',
					slug: item?.slug || '',
					description: item?.description || '',
					banner: item?.banner || '',
					poster: item?.poster || '',
					small_banner: item?.small_banner || '',
					logo: item?.logo || '',
					status: item?.status || 0,
					is_full_width: item?.is_full_width,
					show_on_menu: item?.show_on_menu,
					order: item?.order || 1,
					landing_page_type: item?.landing_page_type || '',
					entityIds: item?.page_selection_entity_links?.length > 0 ? item.page_selection_entity_links[0] : null,
				});


				setLink(
					`${process.env.REACT_APP_BASE_URL}api/v1/mobile/products-selection/${item.slug}/1/50`
				);
			} else {
				alert.error("Error", result.message);
			}
		} catch (error) {
			console.error('Error fetching selection page:', error);
			alert.error("Error", "Error loading page data");
		} finally {
			setLoading(false);
		}
	}, []);

	// Create selection page
	const createSelectionPage = useCallback(async () => {
		const body = {
			...formData,
			page_type: pageType,
			data: blocks,
			entityIds: formData.entityIds ? [formData.entityIds.id] : []
		};

		try {
			const response = await API.adminSetupProductsSelection(body);
			const result = await response.json();

			if (result.error) {
				alert.error("Error", result.message);
				return;
			}

			setLink(
				`${process.env.REACT_APP_BASE_URL}api/v1/mobile/products-selection-admin/${result.data.data?.page?.slug}`
			);

			resetData();

			alert.close();
			alert.toast("Selection page updated successfully", "success", 2000);
			if (pageType === 'landing_page') {
				history.push('/admin/landing-page/list');
			} else {
				history.push('/admin/partner-page/list');
			}
		} catch (error) {
			console.error('Error creating selection page:', error);
			alert.error("Error", error.message);

		}
	}, [formData, pageType, blocks, resetData, history]);

	// Update selection page
	const updateSelectionPage = useCallback(async () => {
		const body = {
			...formData,
			page_type: pageType,
			data: blocks,
			modify,
			entityIds: formData.entityIds ? [formData.entityIds.id] : []
		};

		try {
			const response = await API.adminUpdateProductsSelection(pageId, body);
			const result = await response.json();

			if (result.error) {
				alert.error("Error", result.message);
				return;
			}

			getProductSelectionById(pageId);
			setLink(
				`${process.env.REACT_APP_BASE_URL}api/v1/mobile/products-selection/${result.data.data.update.slug}/1/50`
			);

			resetData();
			alert.close();
			alert.toast("Selection page create successfully", "success", 2000);
		} catch (error) {
			console.error('Error updating selection page:', error);
			alert.error("Error", error.message);
		}
	}, [formData, pageType, blocks, modify, pageId, resetData, getProductSelectionById]);

	const removeHTMLRegex = (htmlString) => {
		try {
			return htmlString
				.replace(/<[^>]*>/g, '') // Remove all HTML tags
				.replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
				.replace(/&amp;/g, '&') // Replace &amp; with &
				.replace(/&lt;/g, '<') // Replace &lt; with
				.replace(/&gt;/g, '>') // Replace &gt; with >
				.replace(/&quot;/g, '"') // Replace &quot; with "
				.replace(/&#39;/g, "'") // Replace &#39; with '
				.trim();
		} catch (e) {
			return htmlString;
		}
	};
	return {
		// State
		blocks,
		formData,
		loading,
		link,
		pageType,
		type,
		itemCount,

		// Form operations
		updateFormField,
		generateSlug,
		uploadImage,

		// Block operations
		addBlock,
		removeBlock,

		// Condition operations
		addCondition,
		removeCondition,
		updateCondition,
		handleMultiSelectChange,

		// Utilities
		formatValuesForMultiSelect,
		checkItemCount,
		checkItemOOS,

		// API operations
		createSelectionPage,
		updateSelectionPage,
		getProductSelectionById,
		removeHTMLRegex,
		handleChangeEntityType,
		handleRemoveEntityType,
		handleAddSKU,
	};
};

export default useSelectionPage;